import { useEffect, useState } from 'react';
import { supabase } from '../../../lib/supabase';
import type { AttachCandidate, AttachKind, FilterState, SortKey, SortDir } from './types';

type Sort = { key: SortKey; dir: SortDir };

export interface UseAttachCandidatesArgs {
  kind: AttachKind;
  activeOrgId: string | null;
  projectId: string | undefined;
  enabled: boolean;
  filters: FilterState;
  sort: Sort;
  page: number;
}

export interface UseAttachCandidatesResult {
  rows: AttachCandidate[];
  total: number;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

const PAGE_SIZE = 50;

/** Translate a Sort into the `list_documents` RPC sort token. */
function docSortToken(sort: Sort): string {
  const dir = sort.dir === 'asc' ? 'asc' : 'desc';
  switch (sort.key) {
    case 'title':
      return `title_${dir}`;
    case 'created':
      return `created_at_${dir}`;
    case 'updated':
      return `last_event_at_${dir}`;
    default:
      return 'last_event_at_desc';
  }
}

/** Resolve the org member id→display_name map. */
async function fetchMemberMap(orgId: string): Promise<Map<string, string>> {
  const { data } = await supabase.rpc('list_org_members', { p_org_id: orgId });
  const map = new Map<string, string>();
  for (const r of (data ?? []) as { user_id?: string | null; display_name?: string | null }[]) {
    if (r.user_id) map.set(r.user_id, r.display_name ?? '');
  }
  return map;
}

/** Resolve the document type id→name map. */
async function fetchTypeMap(orgId: string): Promise<Map<string, string>> {
  const { data } = await supabase
    .from('document_types')
    .select('id, name')
    .eq('org_id', orgId)
    .is('archived_at', null);
  const map = new Map<string, string>();
  for (const r of (data ?? []) as { id: string; name: string }[]) {
    map.set(r.id, r.name);
  }
  return map;
}

interface ListDocumentsRow {
  id: string;
  type_id: string | null;
  title: string | null;
  body_preview: string | null;
  owner_id: string | null;
  lifecycle: string | null;
  last_event_at: string;
  created_at: string;
  total_count?: number | null;
}

interface WorkItemRow {
  id: string;
  name: string | null;
  type: string;
  status: string | null;
  assignee_id: string | null;
  created_at: string;
  updated_at: string;
}

/** Candidate items for the attach-existing modal, normalized across kinds. */
export function useAttachCandidates(
  args: UseAttachCandidatesArgs
): UseAttachCandidatesResult {
  const { kind, activeOrgId, projectId, enabled, filters, sort, page } = args;
  const [rows, setRows] = useState<AttachCandidate[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    if (!activeOrgId || !projectId || !enabled) {
      setRows([]);
      setTotal(0);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);

    (async () => {
      const memberMap = await fetchMemberMap(activeOrgId);

      if (kind === 'documents') {
        const typeMap = await fetchTypeMap(activeOrgId);
        const includeArchived =
          filters.lifecycles.length === 0 || filters.lifecycles.includes('archived');

        const [docsRes, linkedRes] = await Promise.all([
          supabase.rpc('list_documents', {
            p_org_id: activeOrgId,
            p_q: filters.q || null,
            p_type_ids: filters.typeIds.length ? filters.typeIds : null,
            p_owner_ids: filters.ownerIds.length ? filters.ownerIds : null,
            p_created_after: filters.createdAfter ?? null,
            p_created_before: filters.createdBefore ?? null,
            p_include_archived: includeArchived,
            p_sort: docSortToken(sort),
            p_page: page,
            p_page_size: PAGE_SIZE
          }),
          supabase
            .from('project_documents')
            .select('document_id')
            .eq('project_id', projectId)
        ]);
        if (cancelled) return;

        const docRows = (docsRes.data ?? []) as ListDocumentsRow[];
        const nextTotal = docRows[0]?.total_count ?? 0;
        const attached = new Set(
          ((linkedRes.data ?? []) as { document_id: string }[]).map((r) => r.document_id)
        );

        const mapped: AttachCandidate[] = docRows
          .filter((d) => !attached.has(d.id))
          .filter(
            (d) =>
              filters.lifecycles.length === 0 ||
              filters.lifecycles.includes(d.lifecycle ?? '')
          )
          .map((d) => ({
            id: d.id,
            title: d.title || 'Untitled',
            typeLabel: d.type_id ? typeMap.get(d.type_id) ?? undefined : undefined,
            ownerName: d.owner_id ? memberMap.get(d.owner_id) ?? null : null,
            lifecycle: d.lifecycle,
            updatedAt: d.last_event_at,
            createdAt: d.created_at,
            bodyExcerpt: d.body_preview ?? undefined
          }));

        setRows(mapped);
        // total = server-side total_count: the full candidate universe across ALL
        // pages (drives the paginated "X of Y, there are more" count). This is
        // intentionally NOT the length of `mapped`: the already-attached exclusion
        // above can make the shown page smaller than `total` by design, and later
        // pages hold the rest of the universe.
        setTotal(Number(nextTotal));
        setLoading(false);
        return;
      }

      // epics / work-items — direct query, client-side filter + sort.
      const base = supabase
        .from('work_items')
        .select('id, name, type, status, assignee_id, created_at, updated_at')
        .eq('org_id', activeOrgId)
        .is('project_id', null)
        .is('parent_work_item_id', null);
      const res =
        kind === 'epics'
          ? await base.eq('type', 'epic')
          : await base.in('type', ['story', 'bug', 'task']);
      if (cancelled) return;

      let wiRows = (res.data ?? []) as WorkItemRow[];
      const q = filters.q.trim().toLowerCase();

      // Owner facet filter runs on the RAW row's `assignee_id` (the owner facet
      // supplies `filters.ownerIds` as `user_id` values from list_org_members;
      // see facets.ts). A row passes when no owner is selected, or its
      // assignee_id is among the selected owners. Applied pre-map because the
      // mapped AttachCandidate carries `ownerName`, not the raw assignee_id.
      if (filters.ownerIds.length) {
        wiRows = wiRows.filter(
          (w) => w.assignee_id !== null && filters.ownerIds.includes(w.assignee_id)
        );
      }

      let mapped: AttachCandidate[] = wiRows.map((w) => ({
        id: w.id,
        title: w.name || 'Untitled',
        typeLabel: kind === 'epics' ? undefined : w.type,
        ownerName: w.assignee_id ? memberMap.get(w.assignee_id) ?? null : null,
        lifecycle: w.status,
        updatedAt: w.updated_at,
        createdAt: w.created_at
      }));

      if (q) mapped = mapped.filter((c) => c.title.toLowerCase().includes(q));
      if (filters.lifecycles.length > 0) {
        mapped = mapped.filter((c) =>
          filters.lifecycles.includes(c.lifecycle ?? '')
        );
      }
      if (filters.typeIds.length && kind === 'work-items') {
        mapped = mapped.filter(
          (c) => c.typeLabel !== undefined && filters.typeIds.includes(c.typeLabel)
        );
      }

      const dir = sort.dir === 'asc' ? 1 : -1;
      mapped.sort((a, b) => {
        let av: string;
        let bv: string;
        switch (sort.key) {
          case 'title':
            av = a.title;
            bv = b.title;
            break;
          case 'type':
            av = a.typeLabel ?? '';
            bv = b.typeLabel ?? '';
            break;
          case 'owner':
            av = a.ownerName ?? '';
            bv = b.ownerName ?? '';
            break;
          case 'created':
            av = a.createdAt;
            bv = b.createdAt;
            break;
          case 'updated':
          default:
            av = a.updatedAt;
            bv = b.updatedAt;
            break;
        }
        const primary = av.localeCompare(bv);
        const cmp = primary !== 0 ? primary : a.id.localeCompare(b.id);
        return cmp * dir;
      });

      setRows(mapped);
      // total = post-filter match count from a SINGLE client-side fetch (no
      // pagination for work-items/epics): every candidate is already in `mapped`,
      // so the shown rows and `total` are the same set. Contrast the documents
      // branch above, whose `total` is the server's cross-page total_count.
      setTotal(mapped.length);
      setLoading(false);
    })().catch((e: unknown) => {
      if (cancelled) return;
      setError(e instanceof Error ? e.message : 'Failed to load candidates');
      setLoading(false);
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    kind,
    activeOrgId,
    projectId,
    enabled,
    JSON.stringify(filters),
    sort.key,
    sort.dir,
    page,
    nonce
  ]);

  return {
    rows,
    total,
    loading,
    error,
    refetch: () => setNonce((n) => n + 1)
  };
}
